<template>
  
  <div id="mainApp">
    
    <!-- Navigation bar -->
    <NavigationBar v-if="isThereNavbar" :style="`height: ${navbarHeight}px`"/>

    <!-- Page content (router view) -->
    <ScrollPanel style="width: 100%;" :style="`height: ${windowHeight-(isThereNavbar ? navbarHeight : 0)-(isWebView ? footerWebViewHeight : 0)}px;`">

      <div v-if="!httpAutoLoginLoading" class="w-full h-full flex flex-column justify-content-between">
        <router-view class="p-5 pt-2"/>
        <!-- Footer -->
        <!-- <Footer/> -->
      </div>

      <div v-else class="w-full h-full flex align-items-center justify-content-center">
          <LoadingSpinner/>
      </div>
      
    </ScrollPanel>

    <!-- Navigation bar -->
    <FooterWebView v-if="isWebView" :style="`height: ${footerWebViewHeight}px`"/> <!-- It only shows when in the application web view, but it something empty (buttons are not shown), because the buttons will be in the ObjC application, on top of the web content -->

    <!-- <CookiesRGPD v-if="!isWebView"/> -->

  </div>
  <Toast/> <!-- Toast ready to receive all toast alerts -->
</template>

<script setup>
// --- IMPORTS ---
import { ref, onMounted, onBeforeUnmount, watchEffect, computed } from 'vue';
// PrimeVue
import ScrollPanel from 'primevue/scrollpanel';
import Toast from 'primevue/toast';
// vue
import NavigationBar from '@/components/NavigationBar/NavigationBar.vue';
import Footer from '@/components/Footer/Footer.vue';
import FooterWebView from '@/components/Footer/FooterWebView.vue';
import LoadingSpinner from '@/components/Modules/LoadingSpinner.vue';
import CookiesRGPD from '@/components/Others/CookiesRGPD.vue';
// Others
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex';
import UserUtils from '@/composables/UserUtils'

const store = useStore()
const router = useRouter()
const route = useRoute();
const { loginUser } = UserUtils(store, router)

// API
import useApiRequest from '@/composables/HTTPS/useApiRequest';
const { setupUseApiRequest } = useApiRequest()
setupUseApiRequest(store, router)
const { fetchAutoLogin } = useApiRequest()
const httpAutoLoginLoading = ref(false)
const httpAutoLoginData = ref(null)

// Toast
import toastService from '@/composables/toastService';


// --- isWebView info ---
const isWebView = computed(() => store.state.applicationWebView.isWebView)
// --- User info ---
const user = computed(() => store.state.user.user)

// --- USER : IF NOT LOGGED IN, TRY TO /remember-me ---
let autoLoginCheckDone = false
onMounted(async () => {
    // Initialize user
    store.commit('licenses/clearLicenses')
    store.commit('users/clearUsers')
    store.commit('user/clearUser')
})
const isLoggedIn = computed(() => store.getters['user/isLoggedIn'])
watchEffect(async () => { // It makes the remember-me request only once, and only if the path belongs to the array below (for example, we don't want to make a remember-me request on a registerconfirm confirmation link ...)
if (!isLoggedIn.value && !autoLoginCheckDone && ["/login", "/register", "/licenses", "/profile", "/application/login"].includes(route.path)) {
    autoLoginCheckDone = true
    // Send HTTP request
    httpAutoLoginLoading.value = true
    try {
        httpAutoLoginData.value = await fetchAutoLogin()
        // Login the user
        loginUser(httpAutoLoginData.value)
        // Redirect to /licenses
        router.push({ path: '/licenses' })
        // toastService.add({ severity: 'success', summary: `Welcome back, ${store.getters['user/firstName']}!`, detail: 'You have been automatically logged in.', life: 5000 });
    } catch (err) {
        // Automatic remember-me login failed : nothing to do ...
    } finally {
        httpAutoLoginLoading.value = false
    }
  }
});

// --- THEME MODE ---
const storedThemeMode = localStorage.getItem('theme-mode')
if (storedThemeMode) {  store.dispatch('theme/goToTheme', storedThemeMode) }
else { store.dispatch('theme/goToTheme', 'auto') }

// LIGHT/DARK modes from Mac if auto
const handleColorSchemeChange = (event) => {
  if (store.getters['theme/mode'] === 'auto') { store.commit('theme/setIsDark', event.matches); }
};

onMounted(() => {
  const darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  darkModeMediaQuery.addListener(handleColorSchemeChange);
  handleColorSchemeChange(darkModeMediaQuery);
})

// --- Navbar height + Footer height + Scrollpanel responsivity ---
const isThereNavbar = computed(() => !isWebView.value || (isWebView.value && user.value))
const navbarHeight = ref(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ? 55 : 75)
const footerWebViewHeight = ref(65)

// Window height tracking for scrollpanel responsivity
const windowHeight = ref(window.innerHeight);
const updateWindowHeight = () => {
  windowHeight.value = window.innerHeight;
};
onMounted(() => {
  window.addEventListener('resize', updateWindowHeight);
});
onBeforeUnmount(() => {
  window.removeEventListener('resize', updateWindowHeight);
});

// --- WebView: ALERTS FROM THE APPLICATION ---
window.appTriggersAnAlert = async (alertInfoString) => {
  const alertInfo = JSON.parse(alertInfoString) // Parsing the json string
  const alertSeverity = alertInfo.severity ? alertInfo.severity : "error"
  const alertSummary = alertInfo.summary ? alertInfo.summary : "Unknown Error"
  const alertDetail = alertInfo.detail ? alertInfo.detail : "Please try again later."
  const alertLifeTemp = alertInfo.life ? alertInfo.life : "5000"
  const alertLife = /^[0-9]+$/.test(alertLifeTemp) ? alertLifeTemp : "5000"
  toastService.add({ severity: alertSeverity, summary: alertSummary, detail: alertDetail, life: alertLife });
}

// --- TURNSTILE ERROR HANDLING ---
function handleTurnstileError() {
  toastService.add({ severity: "error", summary: "Network issue", detail: "It seems you either are not connected to the internet or have a bad connection. Please try to refresh the page.", life: 8000 });
}
</script>

<style>
/* TEXT */
.text-serial-number {
  font-family: var(--my-font-family-serial-number);
}
/*  */
/* ACTIONS BAR */
.p-divider.p-divider-vertical:before {
  border-left: 1px solid var(--theme-color5);
}
/* DATATABLE */
.p-datatable-wrapper {
    border-radius: var(--my-border-radius);
}
.p-datatable {
    border: 1px solid var(--table-border-color);
    border-radius: var(--my-border-radius);
}
.p-datatable th, .p-datatable tbody td {
    color: var(--theme-font-color);
}
.p-datatable .p-datatable-tbody > tr {
  background-color: var(--table-row-color)
}
.p-datatable .p-datatable-tbody .p-datatable-emptymessage td {
    padding: 0rem 0rem !important;
}
.p-datatable {
    background-color: var(--table-row-color);
}
.p-datatable .p-datatable-thead th {
    background-color: var(--table-header-color);
    border-color: var(--table-header-color);
}
.datatable-license .p-datatable-thead th {
    height: 3.8rem;
}
.p-datatable.p-datatable-scrollable > .p-datatable-wrapper > .p-datatable-table > .p-datatable-thead {
    background-color: var(--table-header-color);
}
.p-datatable .p-datatable-tbody td {
    padding: 0.3rem 0.4rem;
    border-color: transparent;
    font-size: var(--table-data-font-size);
    font-weight: var(--table-data-font-weight);
}
.datatable-license .p-column-title {
  font-size: var(--maintable-column-title-font-size);
  font-weight: var(--maintable-column-title-font-weight);
}
.p-datatable .p-column-title:not(.datatable-license .p-column-title) {
  font-size: var(--othertable-column-title-font-size);
  font-weight: var(--othertable-column-title-font-weight);
}
.p-datatable .p-datatable-tbody > tr.p-highlight {
  color: var(--theme-font-color); /* just overwrite the  code in the themes that changes the color when selected */
}
.p-datatable .row {
    background-color: var(--table-row-color);
}
/* TOOLTIP */
.p-tooltip {
  max-width: 40rem; 
}
/* CONFIRMATION WINDOWS */
.confirmation-window {
    width: 40vw;
    background-color: var(--popup-background-color);
}
.confirmation-window .header-banner {
  background-color: var(--popup-header-banner-background-color);
  color: var(--popup-header-banner-font-color);
}
.confirmation-window .header h2 {
    width: 100%;
    text-align: center;
}
.confirmation-window .close-icon:hover {
    cursor: pointer;
}
.confirmation-window .content-subtitle {
    font-weight: bold;
    margin-top: 15px;
    margin-bottom: 3px;
}
.confirmation-window .content-line {
    margin: 2px;
}
/* BUTTONS */
.p-button:not(.selector .p-button) {
  border-radius: var(--button-border-radius);
  transition: all 0.3s ease-out;
}
.p-button:not(.selector .p-button):not(.license-side-buttons .p-button):not(.delete-user-button .p-button) {
  padding: 0rem 0.9rem;
  height: var(--button-height);
}
.p-button .p-button-label, .p-button span {
  font-size: var(--button-font-size);
  font-weight: var(--button-secondary-font-weight);
}
.p-button.button-small .p-button-label, .p-button span {
  font-size: var(--button-small-font-size);
}
.p-button:not(.p-button-outlined):not(.p-button-text) {
  color: var(--button-primary-font-color);
}
.p-button-outlined:not(.p-button-success):not(.p-button-danger), .p-button-text:not(.p-button-success):not(.p-button-danger) {
  color: var(--button-normal-color);
}
.p-button-outlined:not(.p-button-success):not(.p-button-danger).p-disabled, .p-button-text:not(.p-button-success):not(.p-button-danger).p-disabled {
  color: var(--button-normal-color);
}
.p-button:not(.p-button-outlined):not(.p-button-text):not(.p-button-success):not(.p-button-danger):not(.filters .selector .p-button) {
  background-image: linear-gradient(var(--button-normal-color-g), var(--button-normal-color));
  border: 1px solid var(--button-normal-color);
}
.p-button-outlined:not(.p-button-success).p-button-danger, .p-button-text:not(.p-button-success).p-button-danger {
  color: var(--button-danger-color);
}
.p-button:not(.p-button-outlined):not(.p-button-text):not(.p-button-success):is(.p-button-danger):not(.filters .selector .p-button) {
  background-image: linear-gradient(var(--button-danger-color-g), var(--button-danger-color));
  border: 1px solid var(--button-danger-color);
}
.p-button:not(.p-button-outlined):not(.p-button-text):not(.p-button-success):not(.p-button-danger):not(.filters .selector .p-button).p-disabled {
  opacity: 0.5;
  background-image: linear-gradient(var(--button-disabled-color), var(--button-disabled-color)) !important;
  border: 1px solid var(--button-disabled-color) !important;
}
.p-button.p-button-outlined.p-disabled, .p-button.p-button-text.p-disabled {
  opacity: 0.8 !important;
  color: var(--button-disabled-color) !important;
}
.p-button.p-button-outlined.p-disabled svg, .p-button.p-button-text.p-disabled svg {
  fill: var(--button-disabled-color) !important;
}
.p-button-label {
  font-weight: 100;
  font-size: 1rem;
}
.button-important .p-button-label {
  font-weight: 700 !important;
}
/* InputText / SelectButton / Dropdown */
.input-text-label {
  font-size: var(--popup-subtitle-font-size);
  font-weight: var(--popup-subtitle-font-weight);
  opacity: 0.7;
  margin-top: 15px;
  margin-bottom: 3px;
}
.p-inputtext {
  border-radius: var(--inputcomponent-border-radius);
}
.p-inputtext, .p-dropdown-label {
  font-size: var(--inputcomponent-normal-font-size);
  font-weight: var(--inputcomponent-font-weight);
  padding: 0rem 0.5rem;
  height: var(--inputcomponent-height) !important;
  /* padding: 0.5rem 1.5rem; */
}
.p-inputtext div, .p-dropdown-item div {
  padding: 0;
}
.p-inputtext.inputtext-big, .inputtext-big .p-inputtext, .dropdown-big .p-dropdown-label {
  font-size: var(--inputcomponent-big-font-size);
  font-weight: var(--inputcomponent-font-weight);
  padding: 0rem 0.7rem !important;
  height: 2.5rem !important;
}
.p-inputtext:not(.p-dropdown-label.p-inputtext) {
  background-color:  var(--inputcomponent-color1);
  color: var(--theme-font-color);
}
.p-inputtext:not(.p-dropdown-label.p-inputtext):not(:focus):not(.p-invalid) {
  border-color:  var(--inputcomponent-color3);
}
.p-inputtext:enabled:focus:not(.p-invalid) {
  border-color:  var(--inputcomponent-focus-color);
  box-shadow: 0px 0px 2px var(--inputcomponent-focus-color);
}
.p-inputtext.p-invalid {
  border-color:  var(--inputcomponent-invalid-color);
  box-shadow: 0px 0px 2px var(--inputcomponent-invalid-color);
}
input::placeholder {
  color: var(--theme-font-color);
  opacity: 0.65;
}
.p-selectbutton .p-button:not(.p-highlight) {
  background-color:  var(--inputcomponent-color1);
}
.p-selectbutton .p-button:not(.p-disabled):not(.p-highlight):hover {
  background-color:  var(--inputcomponent-color2);
}
.p-selectbutton .p-button.p-highlight, .p-dropdown-item.p-highlight {
  background-color: var(--inputcomponent-highlight-item-color);
  color: var(--inputcomponent-highlight-item-font-color);
}
.p-selectbutton .p-button, .p-dropdown {
  border-color: var(--inputcomponent-color3);
}
.p-dropdown {
  border-color: var(--inputcomponent-color3);
  background-color: var(--inputcomponent-color1);
}
.p-dropdown.p-focus {
  border-color:  var(--inputcomponent-focus-color);
  box-shadow: 0px 0px 2px var(--inputcomponent-focus-color);
}
.p-dropdown-panel {
  border-color: var(--inputcomponent-color3);
  background-color: var(--inputcomponent-color1);
}
.p-dropdown-header {
  background-color: var(--inputcomponent-color1);
}
.p-dropdown-items {
  padding: 0;
}
.p-dropdown-item {
  color: var(--theme-font-color);
  padding: 0.16rem 0.35rem;
}
.p-dropdown .p-placeholder {
  color: var(--theme-font-color);
  opacity: 0.65;
}
/* STATES */
.p-tag.is-active-user {
    background-color: var(--state-inuse-color);
    color: var(--avatar-inuse-font-color);
}
.p-avatar.is-active-user {
    background-color: var(--state-inuse-color) !important;
    color: var(--avatar-inuse-font-color);
}
/* TAGS */
.p-tag.tag-available {
    border: 1px solid var(--state-available-color);
    background-color: var(--state-available-background-color);
}
.p-tag.tag-available span {
    color: var(--state-available-color);
}
.p-tag.tag-inuseold {
    border: 1px solid var(--state-inuseold-color);
    background-color: var(--state-inuseold-background-color);
}
.p-tag.tag-inuseold span {
    color: var(--state-inuseold-color);
}
.p-tag.tag-inuse {
    border: 1px solid var(--state-inuse-color);
    background-color: var(--state-inuse-background-color);
}
.p-tag.tag-inuse span {
    color: var(--state-inuse-color);
}
.p-tag.tag-inusehere {
    border: 1px solid var(--state-inusehere-color);
    background-color: var(--state-inusehere-background-color);
}
.p-tag.tag-inusehere span {
    color: var(--state-inusehere-color);
}
.p-tag.tag-force-released {
    border: 1px solid var(--state-force-released-color);
    background-color: var(--state-force-released-background-color);
}
.p-tag.tag-force-released span {
    color: var(--state-force-released-color);
}
.p-tag.tag-added-removed {
    border: 1px solid var(--state-added-removed-color);
    background-color: var(--state-added-removed-background-color);
}
.p-tag.tag-added-removed span {
    color: var(--state-added-removed-color);
}
/* SCROLLPANEL */
.horizontal-scrollpanel-table {
    z-index: 0;
    width: 100%;
}
.horizontal-scrollpanel-table-description .p-scrollpanel-content {
    padding: 0px 0px;
    padding-right: 1.3rem;
    margin: 0;
}
.horizontal-scrollpanel-table-delegates .p-scrollpanel-content {
    padding: 5px 0px;
    padding-right: 1.3rem;
    padding-top: 0;
}
.horizontal-scrollpanel-table .p-scrollpanel-wrapper {
    z-index: 0;
}
.horizontal-scrollpanel-table .p-scrollpanel-bar-x {
    height: 3px;
    z-index: 0;
}
.p-scrollpanel-bar-y {
  width: 7.5px;
}
.p-scrollpanel-bar-x, .p-scrollpanel-bar-y {
  opacity: 1;
  background-color: var(--scrollbar-color);
  transition: background-color 0.13s ease-in;
}
.p-scrollpanel-bar-x:hover, .p-scrollpanel-bar-y:hover {
  background-color: var(--scrollbar-hover-color);
}
/* CHECKBOXES */
.p-checkbox .p-checkbox-box {
    border: 1px solid var(--checkbox-background-color);
    color: var(--checkbox-icon-color);
    background-color: var(--checkbox-idle-background-color);
    font-weight: bolder;
}
.p-checkbox .p-icon, .p-checkbox .pi{
    color: var(--checkbox-icon-color) !important;
}
.p-checkbox .p-highlight {
    background-color: var(--checkbox-background-color);
}
/* LABEL */
.potentially-long-label {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
}
/* DATAVIEW */
.p-dataview .p-dataview-content {
    background-color: transparent;
}
/* Default EMPTY content */
.p-dataview-emptymessage, .p-datatable-emptymessage {
    color: var(--theme-font-color);
    opacity: 0.65;
    font-size: var(--font-size-medium);
    font-weight: var(--font-weight-thin);
}
/* BADGE */
.p-badge {
  color: var(--theme-background-color);
  background-color: var(--button-normal-color);
}
/* POPUPS (= dialogs) */
.p-dialog {
  /* border rounder, border stroke, shadow */
  border-radius: var(--popup-border-radius);
  border: none;
  box-shadow: 0px 0px var(--popup-box-shadow) var(--popup-shadow-color);
}
.p-dialog-mask {
  backdrop-filter: blur(2px);
}
.license-popup {
  width: 70rem;
  min-width: 30rem;
}
.add-popup {
  min-width: 18rem;
  width: 75vw;
  max-width: 35rem;
}
.action-popup {
  min-width: 5rem;
  width: 85vw;
  max-width: 50rem;
}
.password-popup {
  width: 25rem;
}
.dialog-header, .dialog-header-danger, .dialog-content, .dialog-footer {
  padding: 1rem;
  display: flex;
  border: 1.5px solid var(--popup-border-color);
}
.dialog-header, .dialog-header-danger {
  flex-direction: row;
  justify-content: center;
  align-items: center;
  font-size: larger;
  font-weight: bold;
  /* height: 4rem; */
  border-top-left-radius: var(--popup-border-radius);
  border-top-right-radius: var(--popup-border-radius);
  border-bottom: none;
  font-size: var(--popup-header-font-size);
  font-weight: var(--popup-header-font-weight);
}
.dialog-header {
  background-color: var(--popup-header-background-color);
  color: var(--popup-header-font-color);
}
.dialog-header-danger {
  background-color: var(--popup-header-danger-background-color);
  color: var(--popup-header-danger-font-color);
  gap: 2rem;
}
.dialog-content {
  background-color: var(--popup-content-background-color);
  flex-direction: column;
  padding-top: 1.5rem;
  padding-bottom: 1.5rem;
  color: var(--theme-font-color);
  border-top: none;
  border-bottom: none;
}
.dialog-content .subtitle {
  font-size: var(--popup-subtitle-font-size);
  font-weight: var(--popup-subtitle-font-weight);
  margin-top: 15px;
  margin-bottom: 3px;
}
.dialog-content .text, .dialog-footer .text {
  font-size: var(--popup-text-font-size);
  font-weight: var(--popup-text-font-weight);
}
.dialog-content .subtext {
  font-size: var(--popup-subtext-font-size);
  font-weight: var(--popup-subtext-font-weight);
}
.dialog-content .smalltext, .dialog-footer .smalltext {
  font-size: var(--popup-smalltext-font-size);
  font-weight: var(--popup-smalltext-font-weight);
}
.dialog-footer {
  background-color: var(--popup-footer-background-color);
  color: var(--popup-footer-font-color);
  flex-direction: row;
  align-items: center;
  /* height: 4rem; */
  border-bottom-left-radius: var(--popup-border-radius);
  border-bottom-right-radius: var(--popup-border-radius);
  border-top: none;
}
.dialog-footer .buttons {
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: end;
  align-items: center;
  gap: 1rem;
}
/* Popup scaling and translation if phone */
@media only screen and (orientation: portrait) { /* VERTICAL */
  @supports (-webkit-touch-callout: none) { /* PHONE */
    .p-dialog {
      transform: scale(0.67) translate(0rem, -2rem);
      max-height: 100%;
    }
    .p-dialog.license-popup {
      transform: scale(0.67) translate(0rem, -3.5rem);
    }
    .p-dialog.add-popup {
      transform: scale(0.7) translate(0rem, 0rem);
    }
    .p-dialog.action-popup:not(.action2-popup):not(.registration-done-popup):not(.forgot-password-done-popup):not(.email-update-done-popup) {
      transform: scale(0.72) translate(0rem, 0rem);
    }
    .p-dialog.action-popup.action2-popup {
      transform: scale(0.6) translate(0rem, -5rem);
    }
    .p-dialog.action-popup.registration-done-popup {
      transform: scale(0.72) translate(0rem, 0rem);
    }
    .p-dialog.action-popup.forgot-password-done-popup {
      transform: scale(0.72) translate(0rem, 0rem);
    }
    .p-dialog.action-popup.email-update-done-popup {
      transform: scale(0.72) translate(0rem, 0rem);
    }
    .p-dialog.password-popup {
      transform: scale(0.67) translate(0rem, -2rem);
    }
  }
}
@media only screen and (orientation: landscape) { /* HORIZONTAL */
  @supports (-webkit-touch-callout: none) { /* PHONE */
    .p-dialog {
      transform: scale(0.36) translate(0rem, -11rem);
      max-height: 100%;
    }
    .p-dialog.license-popup {
      transform: scale(0.36) translate(0rem, -11rem);
    }
    .p-dialog.add-popup {
      transform: scale(0.58) translate(0rem, 0rem);
    }
    .p-dialog.action-popup:not(.action2-popup):not(.registration-done-popup):not(.forgot-password-done-popup):not(.email-update-done-popup) {
      transform: scale(0.4) translate(0rem, -10rem);
    }
    .p-dialog.action-popup.action2-popup {
      transform: scale(0.31) translate(0rem, -14rem);
    }
    .p-dialog.action-popup.registration-done-popup {
      transform: scale(0.67) translate(0rem, -1.5rem);
    }
    .p-dialog.action-popup.forgot-password-done-popup {
      transform: scale(0.54) translate(0rem, -4.5rem);
    }
    .p-dialog.action-popup.email-update-done-popup {
      transform: scale(0.63) translate(0rem, -2rem);
    }
    .p-dialog.password-popup {
      transform: scale(0.54) translate(0rem, -1rem);
    }
  }
}
/* POPUP "RESET" LABEL (attach/detach/licensewindow) */
.reset-text {
    font-size: small;
    font-weight: 100;
    opacity: 0.8;
}
.reset-text:hover {
    cursor: pointer;
}
/* Error message */
.error-message {
    font-size: var(--profile-subtext-font-size);
    font-weight: var(--profile-subtext-font-weight);
}
/* AVATARS */
.p-avatar {
  background-color: var(--avatar-background-color);
}
.p-avatar:not(#user-to-delete .p-avatar) {
  font-size: var(--avatar-font-size);
  font-weight: var(--avatar-font-weight);
}
#user-to-delete .p-avatar {
  font-size: var(--avatar-big-font-size);
  font-weight: var(--avatar-big-font-weight);
}
.avatar {
    margin: 0px 0px;
    padding: 0px 0px;
    transition: all 0.5s ease;
}
.p-datatable .avatar, .dialog-content .avatar {
    transform: scale(0.85, 0.85);
}
.avatar .pi {
    font-weight: 100;
}
.avatar:hover {
    transform: scale(1, 1);
}
.is-user .pi {
    font-weight: 900;
}
.is-selected-user {
    border: 2px solid var(--theme-font-color);
    transform: scale(1, 1) !important;
}
/* Register/Login */
.registerlogin-section {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0.7rem;
  align-items: center;
}
.registerlogin-subsection {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0.1rem;
  align-items: start;
}
/* OverlayPanel */
#user-op .p-overlaypanel-content {
    padding: 1rem !important;
}
/* SVGs */
svg {
  transition: all 0.2s ease;
}
svg.info-svg {
  height: var(--login-info-svg-icon-size)
}
svg.chevron-svg {
  width: var(--chevron-svg-icon-size)
}
svg.pluscircle-svg {
  width: var(--pluscircle-svg-icon-size);
}
svg.checkbox-icon-svg {
  width: var(--checkbox-svg-icon-size);
}
svg.history-arrow-svg {
  width: var(--history-arrow-svg-icon-size);
}
svg.navbar-logo-svg {
  height: var(--navbar-logo-svg-icon-size)
}
svg.navbar-logo-phone-svg {
  height: var(--navbar-logo-phone-svg-icon-size)
}
svg.navbar-menuitem-svg {
  height: var(--navbar-menuitem-svg-icon-size)
}
svg.navbar-user-svg {
  height: var(--navbar-user-svg-icon-size)
}
svg.navbar-userop-avatar-me-svg {
  height: var(--navbar-userop-avatar-me-svg-icon-size)
}
svg.navbar-button-svg {
  height: var(--navbar-button-svg-icon-size)
}
svg.button-svg {
  height: var(--button-svg-icon-size)
}
svg.close-cross-svg {
  height: var(--button-close-svg-icon-size)
}
svg.table-licenses-download-svg {
  height: var(--table-licenses-svg-icon-size)
}
svg.table-licenses-svg:not(.ellipsis) {
  height: var(--table-licenses-svg-icon-size)
}
svg.table-licenses-svg.ellipsis {
  width: var(--table-licenses-svg-icon-size)
}
svg.avatar-me-svg {
  height: var(--avatar-me-svg-icon-size)
}
svg.inputcomponent-left-svg {
  height: var(--inputcomponent-left-svg-icon-size)
}
svg.inputcomponent-right-svg {
  height: var(--inputcomponent-right-svg-icon-size)
}
svg.profile-svg {
  height: var(--profile-svg-icon-size)
}
svg.thememode-svg {
  height: var(--thememode-svg-icon-size)
}
svg.popup-warning-svg{
  height: var(--popup-warning-svg-icon-size)
}
svg.popup-error-svg{
  height: var(--popup-error-svg-icon-size)
}
svg.tag-small-status-warning-svg {
  height: var(--tag-small-status-warning-svg-icon-size)
}
svg.tag-big-status-warning-svg {
  height: var(--tag-big-status-warning-svg-icon-size)
}
svg.registerconfirm-svg, svg.resetpassword-svg{
  height: var(--registerconfirm-svg-icon-size)
}
svg.migration-status-svg{
  height: var(--migration-status-svg-icon-size)
}
svg.upgrade-svg{
  height: var(--upgrade-svg-icon-size)
}
</style>

<style scoped>
/* MAIN APP */
#mainApp {
  margin: 0px;
  display: flex;
  flex-direction: column;
  background-color: var(--theme-background-color);
  min-width: 570px;
}
@supports (-webkit-touch-callout: none) {
  #mainApp {
    min-width: 400px !important;
  }
}
</style>
